import { BookOpen, Calendar, Clock2 } from "lucide-react";
import { Button, Divider, Drawer, Modal } from "antd";
import clsx from "clsx";
import { useState } from "react";
import Input from "antd/es/input/Input";
import TutorChatBox from "./TutorChatBox";
import { useGetSingleBookingQuery } from "../../../hooks/api/Students/Bookings/useGetSingleBookingQuery";
import { useSendConversationsMutation } from "../../../hooks/api/Tutors/Conversation/useSendConversationsMutation";
import BookingDetails from "./BookingDetails";
import { useConfirmTutorBooking } from "../../../hooks/api/Tutors/Bookings/useConfirmTutorBooking";
import Loader from "../../../components/Loader";
import AvailabilityBadge from "../../Students/Tutors/Components/AvailabilityBadge";
import { MessageOutlined } from "@ant-design/icons";
import BookingDetailsTable from "./BookingTable";
import Moment from "react-moment";
import ChatBox from "./TutorChatBox";

type Props = {
  id: string;
  subject?: any;
  tutorShipType?: any;
  date?: any;
  tutor: any;
  timeSlots?: any;
  status?: "confirmed" | "pending" | "cancelled" | string;
  price?: any;
  duration: any;
  notes?: any;
  student?: any;
};

const STATUS_COLORS: Record<string, string> = {
  confirmed: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  cancelled: "bg-red-100 text-red-700",
  default: "bg-gray-100 text-gray-700",
};

const TutorSessionCard = ({
  _id,
  subject,
  subjectName,
  gradeCategory,
  tutorShipType,
  student,
  tutor,
  date,
  startUtc,
  endUtc,
  timeSlots,
  status,
  price,
  duration,
  notes,
  isPaidOut,
  createdAt,
  updatedAt,
  userLocalStart,
  userLocalEnd,
}: any) => {
  const isSessionEnded = (date: string, duration: number) => {
    const sessionEndTime = new Date(date).getTime() + duration * 60000;
    return Date.now() >= sessionEndTime;
  };

  const hasSessionEnded = (startTime: string, duration: number): boolean => {
    const sessionEnd = new Date(startTime).getTime() + duration * 60000;
    return Date.now() >= sessionEnd;
  };

  const [chatVisible, setChatVisible] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState("");
  const [messageInput, setMessageInput] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);

  const [visible, setVisible] = useState("");

  const [selectedId, setSelectedId] = useState("");

  const { mutateAsync: sendMessage, isLoading: isSendingMessage } =
    useSendConversationsMutation({
      id: selectedId,
      setMessageInput,
    });

  const handleChatClick = (id: string) => {
    setSelectedId(id);
    setChatVisible(true);
  };

  const handleConfirmClick = (record: any) => {
    setSelectedBooking(record);
    setIsModalVisible(true);
    setVisible("confirm");
  };

  const handleSendMessage = () => {
    sendMessage({ message: messageInput, sender: "Tutor", type: "text" });
  };

  const [trigger, setTrigger] = useState(false);

  const closeModal = () => {
    setIsModalVisible(!isModalVisible);
  };

  const { isLoading } = useConfirmTutorBooking({
    id: selectedBooking?._id ?? "",
    trigger: !!selectedBooking && trigger,
    closeModal: closeModal,
  });

  const { data: details, isLoading: isDetailsLoading } =
    useGetSingleBookingQuery({ id: selectedId ?? "" });

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedBooking(null);
    setVisible("");
  };

  const formatTimeRange = (timeSlot: any) => {
    const [hour, minute] = timeSlot.split(":").map(Number);
    const nextHour = (hour + 1) % 24;

    const formatTime = (h: number, m: number) => {
      const period = h >= 12 ? "pm" : "am";
      const formattedHour = h % 12 === 0 ? 12 : h % 12;
      return `${formattedHour}:${m.toString().padStart(2, "0")}${period}`;
    };

    return `${formatTime(hour, minute)} - ${formatTime(nextHour, minute)}`;
  };

  const isCurrentTimePastSlot = (timeSlots: string[]): boolean => {
    const currentTime = Date.now();

    // Parse the first time slot from the array
    const [hour, minute] = timeSlots[0].split(":").map(Number);
    const currentTimeSlot = new Date();
    currentTimeSlot.setHours(hour, minute, 0, 0);

    return currentTime >= currentTimeSlot.getTime();
  };
  return (
    <div className="relative p-6 rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div
        className={clsx(
          "absolute top-3 right-3 flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-semibold",
          STATUS_COLORS[status] || STATUS_COLORS.default
        )}
      >
        {status === "confirmed" && (
          <span className="h-2 w-2 bg-green-500 rounded-full animate-ping absolute" />
        )}
        <span className="relative">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>

      <div className=" mt-[32px] mb-6">
        <h3 className="text-lg W2STMedium capitalize">
          Session with{" "}
          <span className="text-primary700 font-semibold">
            {student?.firstName}
          </span>
        </h3>
        {/* <div className="text-gray-600 text-sm">
          <div className="font-medium">${price}</div>
          <div>{duration} mins</div>
        </div> */}
      </div>

      <div className="flex flex-col gap-4 mt-5">
        <span className="flex gap-2.5 items-center group hover:text-primary700 transition-colors cursor-default">
          <BookOpen
            color="currentColor"
            size={20}
            className="group-hover:text-primary700"
          />
          <span className="text-gray-700 capitalize group-hover:text-primary700">
            {subject?.subject} - {subject?.category}
          </span>
        </span>

        <span className="flex gap-2.5 items-center group hover:text-primary700 transition-colors cursor-default">
          <Calendar
            color="currentColor"
            size={20}
            className="group-hover:text-primary700"
          />
          <div className="flex flex-col">
            <span className="text-gray-700 group-hover:text-primary700">
              {new Date(date).toLocaleDateString("en-US", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
            <span className="text-sm text-gray-500">
              <Moment fromNow interval={0}>
                {date}
              </Moment>
            </span>
          </div>
        </span>

        <span className="flex gap-2.5 items-center group hover:text-primary700 transition-colors cursor-default">
          <Clock2
            color="currentColor"
            size={20}
            className="group-hover:text-primary700"
          />
          <div className="flex flex-col">
            <span className="text-gray-700 group-hover:text-primary700">
              <Moment format={"h:mm a"}>{startUtc}</Moment> -{" "}
              <Moment format={"h:mm a"}>{endUtc}</Moment>
            </span>
            <span className="text-sm text-gray-500">
              Duration: {duration} mins
            </span>
          </div>
        </span>

        <span className="flex gap-2.5 items-center group hover:text-primary700 transition-colors cursor-default">
          <svg
            className="group-hover:text-primary700"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
          </svg>
          <div className="flex flex-col">
            <span className="text-gray-700 group-hover:text-primary700 font-medium">
              ${price}
            </span>
            <span className="text-sm text-gray-500">Session fee</span>
          </div>
        </span>
      </div>

      <div className="mt-6">
        <AvailabilityBadge availabilityType={tutorShipType} />
      </div>

      {notes && notes.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-600 font-medium mb-1">Notes:</div>
          <div className="text-gray-700">{notes.join(", ")}</div>
        </div>
      )}

      <Divider className="my-6" />

      <div className="flex flex-wrap gap-3 mt-4">
        {status === "pending" && (
          <Button
            onClick={() =>
              handleConfirmClick({
                _id,
                subject,
                tutorShipType,
                date,
                timeSlots,
                tutor,
                price,
                notes,
                duration,
                status,
              })
            }
            type="primary"
            size="large"
            className="flex-1 min-w-[140px] hover:opacity-90 transition-opacity"
          >
            Confirm Booking
          </Button>
        )}
        <Button
          type="primary"
          size="large"
          icon={<MessageOutlined />}
          disabled={status !== "confirmed"}
          onClick={() => {
            handleChatClick(_id);
            setSelectedTutor(tutor?.firstName);
          }}
          className="flex-1 min-w-[140px] hover:opacity-90 transition-opacity"
        >
          Chat Student
        </Button>
      </div>

      <Modal
        title="Booking Confirmation"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          visible === "confirm" && (
            <Button
              type="primary"
              key="confirm"
              size="large"
              disabled={isLoading || selectedBooking?.status === "confirmed"}
              onClick={() => setTrigger(true)}
              className="hover:opacity-90 transition-opacity"
            >
              Confirm Booking
            </Button>
          ),
        ].filter(Boolean)}
      >
        {selectedBooking && visible === "confirm" && (
          <BookingDetailsTable selectedBooking={selectedBooking} />
        )}
        {selectedBooking && visible === "more" && (
          <div>
            {isDetailsLoading && <Loader />}
            {!isDetailsLoading && details && (
              <div>
                <BookingDetails booking={details?.data} />
              </div>
            )}
          </div>
        )}
      </Modal>

      <Drawer
        title={`Chat with ${student?.firstName}`}
        placement="right"
        width={window.innerWidth > 768 ? 500 : "100%"}
        onClose={() => {
          setChatVisible(false);
          setSelectedTutor("");
        }}
        open={chatVisible}
      >
        <div className="flex flex-col h-[calc(100vh-60px)]">
          <div className="flex-1 overflow-y-auto px-1">
            <ChatBox id={selectedId} />
          </div>
          <div className="sticky bottom-0 bg-white pt-4 pb-6 border-t border-gray-100">
            <div className="relative">
              <textarea
                value={messageInput}
                rows={4}
                onChange={(e) => setMessageInput(e.target.value)}
                placeholder="Type your message here..."
                disabled={isSendingMessage}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                className="w-full min-h-[80px] max-h-[160px] p-4 pr-[100px] rounded-xl border border-gray-200 focus:border-primary700 focus:ring-1 focus:ring-primary700 resize-y placeholder:text-gray-400"
              />
              <Button
                type="primary"
                size="large"
                onClick={handleSendMessage}
                disabled={isSendingMessage || !messageInput.trim()}
                className="absolute bottom-4 right-4 hover:opacity-90 transition-opacity rounded-lg flex items-center gap-2"
              >
                Send
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default TutorSessionCard;
